import { createEffect, createMemo, createSignal, For, Show } from "solid-js";
import { createStore, produce } from "solid-js/store";
import { Link, useNavigate } from "@/lib/u-router";
import { ArrowLeft, CalendarRange, FileClock, History, Link2, Plus, Trash2 } from "lucide-solid";

import { Button } from "@/components/ui/button";
import { Label, TextField } from "@/components/ui/text-field";
import { Select } from "@/components/ui/select";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { NotFoundState } from "@/components/ui/not-found-state";
import { createEntityResolution } from "@/lib/use-entity-resolution";
import { UNIT_OPTIONS } from "@/lib/constants";
import {
  createRecipeMutation,
  deleteRecipeMutation,
  ensureAislesByNameMutation,
  handleApiResponse,
  parseNutritionInput,
  parseRecipeFromHtml,
  parseStringArray,
  type RecipeIngredientInput,
  type RecipeInput,
  restoreRecipeMutation,
  restoreRecipeVersionMutation,
  trashRecipeMutation,
  updateRecipeMutation,
} from "@mealplan/shared";
import { api } from "@/lib/api";
import type { RecipeVersion } from "@mealplan/powersync";
import {
  useAislesQuery,
  useMealPlanByRecipeQuery,
  useRecipeDetailQuery,
  useRecipeIngredientsQuery,
} from "@mealplan/shared/solid";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { useI18n } from "@/providers/i18n-provider";
import { msg } from "@lingui/core/macro";
import { showToast } from "@/components/ui/toast";
import { registerUndo } from "@/lib/undo";
import { undoMessages } from "@/lib/undo-messages";
import { EntityHistoryDialog } from "@/features/activity/components/entity-history-dialog";
import { RecipeVersionHistorySheet } from "./recipe-version-history-sheet";

interface IngredientRow {
  id?: string;
  name: string;
  quantity: string;
  unit: string;
  category: string;
}

const EMPTY_ROW: IngredientRow = { name: "", quantity: "", unit: "", category: "" };
const DEFAULT_SERVINGS = 2;

function parseQuantity(value: string): number | null {
  const trimmed = value.trim().replace(",", ".");
  if (!trimmed) {
    return null;
  }
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

export function RecipeForm(props: { recipeId?: string }) {
  const { t, locale } = useI18n();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const session = useSession();
  const navigate = useNavigate();

  const isEdit = (): boolean => Boolean(props.recipeId);
  const recipeId = (): string => props.recipeId ?? "";

  const detail = useRecipeDetailQuery({ db, drizzle, userId: session.userId, recipeId });
  const ingredients = useRecipeIngredientsQuery({
    db,
    drizzle,
    userId: session.userId,
    recipeId,
  });
  const aisles = useAislesQuery({ db, drizzle, userId: session.userId });
  // Interconexión: ¿en qué entradas del plan se usa esta receta? (Fase 5, eje 6)
  const plannedIn = useMealPlanByRecipeQuery({ db, drizzle, userId: session.userId, recipeId });

  const mealTypeLabel = (meal: string | null): string => {
    switch (meal) {
      case "breakfast": {
        return t(msg`Breakfast`);
      }
      case "lunch": {
        return t(msg`Lunch`);
      }
      case "dinner": {
        return t(msg`Dinner`);
      }
      case "snack": {
        return t(msg`Snack`);
      }
      default: {
        return meal ?? "";
      }
    }
  };

  const formatPlanDate = (dateKey: string | null): string => {
    if (!dateKey) {
      return "";
    }
    const [y = 0, m = 1, d = 1] = dateKey.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString(locale(), {
      weekday: "short",
      day: "numeric",
      month: "short",
    });
  };

  const [name, setName] = createSignal("");
  const [servings, setServings] = createSignal(String(DEFAULT_SERVINGS));
  const [tags, setTags] = createSignal("");
  const [steps, setSteps] = createSignal("");
  const [notes, setNotes] = createSignal("");
  // 🥗 Nutrición POR PORCIÓN (opcional): el planner escala por porciones.
  const [calories, setCalories] = createSignal("");
  const [proteinG, setProteinG] = createSignal("");
  const [carbsG, setCarbsG] = createSignal("");
  const [fatG, setFatG] = createSignal("");
  const [rows, setRows] = createStore<{ list: IngredientRow[] }>({
    list: [{ ...EMPTY_ROW }],
  });
  const [isSaving, setIsSaving] = createSignal(false);
  const [importUrl, setImportUrl] = createSignal("");
  const [importing, setImporting] = createSignal(false);
  const [confirmDelete, setConfirmDelete] = createSignal(false);
  const [historyOpen, setHistoryOpen] = createSignal(false);
  const [versionsOpen, setVersionsOpen] = createSignal(false);
  const [restoringVersion, setRestoringVersion] = createSignal(false);
  const [scalePreview, setScalePreview] = createSignal("");
  const [hydrated, setHydrated] = createSignal(false);

  const aisleNameById = createMemo(
    () => new Map((aisles.data ?? []).map((a) => [a.id, a.name ?? ""] as const)),
  );

  // Hidratar el formulario una única vez al cargar la receta (modo edición).
  createEffect(() => {
    if (!isEdit() || hydrated()) {
      return;
    }
    const recipe = detail.data;
    const ingredientRows = ingredients.data;
    if (!recipe || ingredientRows === undefined) {
      return;
    }
    setName(recipe.name ?? "");
    setServings(String(recipe.servings ?? DEFAULT_SERVINGS));
    setTags(parseStringArray(recipe.tags).join(", "));
    setSteps(parseStringArray(recipe.steps).join("\n"));
    setNotes(recipe.notes ?? "");
    setCalories(
      recipe.calories === null || recipe.calories === undefined ? "" : String(recipe.calories),
    );
    setProteinG(
      recipe.protein_g === null || recipe.protein_g === undefined ? "" : String(recipe.protein_g),
    );
    setCarbsG(
      recipe.carbs_g === null || recipe.carbs_g === undefined ? "" : String(recipe.carbs_g),
    );
    setFatG(recipe.fat_g === null || recipe.fat_g === undefined ? "" : String(recipe.fat_g));
    if (ingredientRows.length > 0) {
      setRows("list", [
        ...ingredientRows.map((row) => ({
          id: row.id,
          name: row.name ?? "",
          quantity: row.quantity === null || row.quantity === undefined ? "" : String(row.quantity),
          unit: row.unit ?? "",
          category: row.aisle_id ? (aisleNameById().get(row.aisle_id) ?? "") : "",
        })),
        { ...EMPTY_ROW },
      ]);
    }
    setHydrated(true);
  });

  // Resuelve la receta en modo edición: presente / cargando (sync) / no-encontrada
  // / offline. En modo "nueva receta" no hay entidad que resolver, así que se trata
  // siempre como presente para renderizar el formulario en blanco.
  const resolution = createEntityResolution({
    db,
    hasEntity: () => !isEdit() || Boolean(detail.data),
    isPending: () => isEdit() && detail.isLoading,
  });

  const scaledIngredients = createMemo(() => {
    const target = parseQuantity(scalePreview());
    const base = Number(servings()) || DEFAULT_SERVINGS;
    if (!target || base <= 0) {
      return null;
    }
    const factor = target / base;
    return rows.list
      .filter((row) => row.name.trim())
      .map((row) => {
        const quantity = parseQuantity(row.quantity);
        return {
          name: row.name,
          unit: row.unit,
          quantity: quantity === null ? null : Math.round(quantity * factor * 100) / 100,
        };
      });
  });

  // Import de receta por URL: el backend actúa de proxy (CORS + SSRF) y trae el
  // HTML; el parser determinista de `@mealplan/shared` extrae el JSON-LD
  // schema.org/Recipe. Sólo PRE-RELLENA el formulario — el usuario revisa y
  // guarda; nada se persiste automáticamente.
  const handleImport = async (): Promise<void> => {
    const url = importUrl().trim();
    if (!url || importing()) {
      return;
    }
    setImporting(true);
    try {
      const res = await handleApiResponse(api.POST("/api/recipes/import", { body: { url } }));
      const draft = parseRecipeFromHtml(res.html);
      if (!draft) {
        showToast(t(msg`We couldn't find a recipe on that page.`), "error");
        return;
      }
      setName(draft.name);
      if (draft.servings) {
        setServings(String(draft.servings));
      }
      setTags(draft.tags.join(", "));
      setSteps(draft.steps.join("\n"));
      if (draft.notes) {
        setNotes(draft.notes);
      }
      if (draft.nutrition.calories !== null) {
        setCalories(String(draft.nutrition.calories));
      }
      if (draft.nutrition.proteinG !== null) {
        setProteinG(String(draft.nutrition.proteinG));
      }
      if (draft.nutrition.carbsG !== null) {
        setCarbsG(String(draft.nutrition.carbsG));
      }
      if (draft.nutrition.fatG !== null) {
        setFatG(String(draft.nutrition.fatG));
      }
      if (draft.ingredients.length > 0) {
        setRows("list", [
          ...draft.ingredients.map((ing) => ({
            name: ing.name,
            quantity: ing.quantity === null ? "" : String(ing.quantity),
            unit: ing.unit ?? "",
            category: "",
          })),
          { ...EMPTY_ROW },
        ]);
      }
      showToast(t(msg`Recipe imported — review and save.`), "success");
    } catch {
      showToast(t(msg`We couldn't import from that URL.`), "error");
    } finally {
      setImporting(false);
    }
  };

  const handleSave = async (): Promise<void> => {
    const dbInstance = drizzle();
    const uid = session.userId();
    const trimmedName = name().trim();
    if (!dbInstance || !uid || !trimmedName || isSaving()) {
      return;
    }
    setIsSaving(true);
    try {
      const categories = rows.list.map((row) => row.category);
      const aisleIdByName = await ensureAislesByNameMutation(dbInstance, uid, categories);

      const ingredientInputs: RecipeIngredientInput[] = rows.list
        .filter((row) => row.name.trim())
        .map((row) => ({
          id: row.id,
          name: row.name,
          quantity: parseQuantity(row.quantity),
          unit: row.unit.trim() || null,
          aisleId: aisleIdByName.get(row.category.trim()) ?? null,
        }));

      const input = {
        name: trimmedName,
        servings: Number(servings()) || DEFAULT_SERVINGS,
        steps: steps()
          .split("\n")
          .map((line) => line.trim())
          .filter(Boolean),
        tags: tags()
          .split(",")
          .map((tag) => tag.trim())
          .filter(Boolean),
        notes: notes(),
        ingredients: ingredientInputs,
        nutrition: {
          calories: parseNutritionInput(calories()),
          proteinG: parseNutritionInput(proteinG()),
          carbsG: parseNutritionInput(carbsG()),
          fatG: parseNutritionInput(fatG()),
        },
      };

      if (isEdit()) {
        // Snapshot PREVIO (campo a campo) para la inversa del undo: re-aplica
        // exactamente los valores de antes de esta edición.
        const previous = detail.data;
        const previousIngredients = ingredients.data ?? [];
        const previousInput: RecipeInput | null = previous
          ? {
              name: previous.name ?? "",
              servings: previous.servings ?? DEFAULT_SERVINGS,
              steps: parseStringArray(previous.steps),
              tags: parseStringArray(previous.tags),
              notes: previous.notes ?? null,
              nutrition: {
                calories: previous.calories ?? null,
                proteinG: previous.protein_g ?? null,
                carbsG: previous.carbs_g ?? null,
                fatG: previous.fat_g ?? null,
              },
              ingredients: previousIngredients.map((row) => ({
                id: row.id,
                name: row.name ?? "",
                quantity: row.quantity ?? null,
                unit: row.unit ?? null,
                aisleId: row.aisle_id ?? null,
              })),
            }
          : null;
        const id = recipeId();
        await updateRecipeMutation(dbInstance, uid, id, input);
        if (previousInput) {
          registerUndo(undoMessages(t, t(msg`Recipe updated`)), () =>
            updateRecipeMutation(dbInstance, uid, id, previousInput),
          );
        }
      } else {
        const newId = await createRecipeMutation(dbInstance, uid, input);
        // Inversa de "crear" = borrado DURO (aún no hay historial que preservar).
        registerUndo(undoMessages(t, t(msg`Recipe created`)), () =>
          deleteRecipeMutation(dbInstance, newId),
        );
      }
      showToast(t(msg`Recipe saved`), "success");
      await navigate({ to: "/recipes" });
    } catch (err: unknown) {
      console.error("Error saving recipe:", err instanceof Error ? err.message : String(err));
      showToast(t(msg`Couldn't save the recipe`), "error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (): Promise<void> => {
    const dbInstance = drizzle();
    if (!dbInstance || !isEdit()) {
      return;
    }
    const id = recipeId();
    // Borrar desde la UI = mover a la papelera (soft-delete): recuperable desde
    // Perfil → Papelera y deshacible al momento con la pill (inversa = restaurar).
    await trashRecipeMutation(dbInstance, id);
    registerUndo(undoMessages(t, t(msg`Recipe moved to trash`)), () =>
      restoreRecipeMutation(dbInstance, id),
    );
    await navigate({ to: "/recipes" });
  };

  // L4: restaurar una versión = escritura NORMAL hacia adelante (reutiliza
  // updateRecipeMutation). El server archiva el estado actual como versión
  // nueva ANTES de aplicarla; NO toca la papelera (deleted_at). El propio
  // updateRecipeMutation registra una actividad "updated" en el feed.
  const handleRestoreVersion = async (version: RecipeVersion): Promise<void> => {
    const dbInstance = drizzle();
    const uid = session.userId();
    if (!dbInstance || !uid || restoringVersion()) {
      return;
    }
    setRestoringVersion(true);
    try {
      await restoreRecipeVersionMutation(dbInstance, uid, recipeId(), version);
      setVersionsOpen(false);
      showToast(t(msg`Version restored`), "success");
    } catch (err: unknown) {
      console.error(
        "Error restoring recipe version:",
        err instanceof Error ? err.message : String(err),
      );
      showToast(t(msg`Couldn't restore the version`), "error");
    } finally {
      setRestoringVersion(false);
    }
  };

  const updateRow = (index: number, field: keyof IngredientRow, value: string): void => {
    setRows(
      "list",
      produce((list) => {
        const row = list[index];
        if (row) {
          row[field] = value;
        }
        // Añadir fila vacía al final cuando se escribe en la última.
        const last = list.at(-1);
        if (last?.name?.trim()) {
          list.push({ ...EMPTY_ROW });
        }
      }),
    );
  };

  return (
    <Show
      when={resolution() === "ready" || resolution() === "loading"}
      fallback={
        <NotFoundState
          variant={resolution() === "offline" ? "offline" : "not-found"}
          title={resolution() === "offline" ? undefined : t(msg`Recipe not found`)}
          description={
            resolution() === "offline"
              ? undefined
              : t(msg`This recipe may have been deleted, or you don't have access to it.`)
          }
          primaryAction={{
            label: t(msg`Back to recipes`),
            onClick: () => {
              void navigate({ to: "/recipes" });
            },
          }}
        />
      }
    >
      <div class="space-y-6">
        <div class="flex items-center justify-between gap-3">
          <div class="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => void navigate({ to: "/recipes" })}
              aria-label={t(msg`Back`)}
            >
              <ArrowLeft class="h-4 w-4" />
            </Button>
            <h2 class="font-serif text-2xl font-semibold tracking-tight">
              {isEdit() ? t(msg`Edit recipe`) : t(msg`New recipe`)}
            </h2>
          </div>
          <div class="flex items-center gap-2">
            <Show when={isEdit()}>
              <Button
                variant="ghost"
                size="sm"
                aria-label={t(msg`Version history`)}
                onClick={() => setVersionsOpen(true)}
                data-testid="recipe-version-history-open"
              >
                <FileClock class="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                aria-label={t(msg`History`)}
                onClick={() => setHistoryOpen(true)}
                data-testid="recipe-history"
              >
                <History class="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                class="text-destructive"
                onClick={() => setConfirmDelete(true)}
                data-testid="delete-recipe"
              >
                <Trash2 class="h-4 w-4" />
              </Button>
            </Show>
            <Button
              size="sm"
              disabled={!name().trim() || isSaving() || !drizzle() || (isEdit() && !detail.data)}
              onClick={() => void handleSave()}
              data-testid="save-recipe"
            >
              {t(msg`Save`)}
            </Button>
          </div>
        </div>

        {/* Import por URL (sólo al crear): pre-rellena desde el JSON-LD de la web. */}
        <Show when={!isEdit()}>
          <div
            class="space-y-2 rounded-xl border border-dashed border-border p-4"
            data-testid="recipe-import"
          >
            <div class="flex items-center gap-2">
              <Link2 class="h-4 w-4 text-accent" />
              <Label for="recipe-import-url">{t(msg`Import from a website`)}</Label>
            </div>
            <p class="text-xs text-muted-foreground">
              {t(
                msg`Paste a recipe link and we'll fill in the form from the page's structured data.`,
              )}
            </p>
            <div class="flex flex-wrap gap-2">
              <TextField
                id="recipe-import-url"
                type="url"
                inputmode="url"
                class="min-w-48 flex-1"
                placeholder="https://…"
                value={importUrl()}
                disabled={importing()}
                onInput={(e) => setImportUrl(e.currentTarget.value)}
                data-testid="recipe-import-url"
              />
              <Button
                variant="outline"
                class="gap-2"
                loading={importing()}
                disabled={!importUrl().trim() || importing()}
                onClick={() => void handleImport()}
                data-testid="recipe-import-submit"
              >
                <Link2 class="h-4 w-4" />
                {t(msg`Import`)}
              </Button>
            </div>
          </div>
        </Show>

        <div class="grid gap-4 sm:grid-cols-2">
          <div class="space-y-2 sm:col-span-2">
            <Label for="recipe-name">{t(msg`Name`)}</Label>
            <TextField
              id="recipe-name"
              value={name()}
              placeholder={t(msg`e.g. Lentil stew`)}
              onInput={(e) => setName(e.currentTarget.value)}
              data-testid="recipe-name"
            />
          </div>
          <div class="space-y-2">
            <Label for="recipe-servings">{t(msg`Servings`)}</Label>
            <TextField
              id="recipe-servings"
              type="number"
              min="1"
              value={servings()}
              onInput={(e) => setServings(e.currentTarget.value)}
              data-testid="recipe-servings"
            />
          </div>
          <div class="space-y-2">
            <Label for="recipe-tags">{t(msg`Tags (comma separated)`)}</Label>
            <TextField
              id="recipe-tags"
              value={tags()}
              placeholder={t(msg`e.g. pasta, quick`)}
              onInput={(e) => setTags(e.currentTarget.value)}
              data-testid="recipe-tags"
            />
          </div>
        </div>

        <div class="space-y-2">
          <Label>{t(msg`Ingredients`)}</Label>
          <div class="space-y-2" data-testid="ingredient-rows">
            <For each={rows.list}>
              {(row, index) => (
                <div class="flex flex-wrap items-center gap-2 ingredient-row">
                  <TextField
                    class="w-20 shrink-0"
                    placeholder={t(msg`Qty`)}
                    value={row.quantity}
                    onInput={(e) => {
                      updateRow(index(), "quantity", e.currentTarget.value);
                    }}
                    data-testid={`ingredient-qty-${index()}`}
                  />
                  <div class="w-24 shrink-0">
                    <Select
                      options={UNIT_OPTIONS}
                      value={row.unit || null}
                      onChange={(value) => {
                        updateRow(index(), "unit", value ?? "");
                      }}
                      placeholder={t(msg`Unit`)}
                      aria-label={t(msg`Unit`)}
                      data-testid={`ingredient-unit-${index()}`}
                    />
                  </div>
                  <TextField
                    class="min-w-40 flex-1"
                    placeholder={t(msg`Ingredient`)}
                    value={row.name}
                    onInput={(e) => {
                      updateRow(index(), "name", e.currentTarget.value);
                    }}
                    data-testid={`ingredient-name-${index()}`}
                  />
                  <TextField
                    class="w-32 shrink-0"
                    placeholder={t(msg`Category`)}
                    value={row.category}
                    list="aisle-suggestions"
                    onInput={(e) => {
                      updateRow(index(), "category", e.currentTarget.value);
                    }}
                    data-testid={`ingredient-category-${index()}`}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    class="shrink-0"
                    aria-label={t(msg`Remove ingredient`)}
                    onClick={() => {
                      setRows(
                        "list",
                        produce((list) => {
                          list.splice(index(), 1);
                          if (list.length === 0) {
                            list.push({ ...EMPTY_ROW });
                          }
                        }),
                      );
                    }}
                  >
                    <Trash2 class="h-4 w-4 text-muted-foreground" />
                  </Button>
                </div>
              )}
            </For>
            <datalist id="aisle-suggestions">
              <For each={aisles.data ?? []}>{(a) => <option value={a.name ?? ""} />}</For>
            </datalist>
          </div>
          <Button
            variant="outline"
            size="sm"
            class="gap-2"
            onClick={() => {
              setRows("list", rows.list.length, { ...EMPTY_ROW });
            }}
          >
            <Plus class="h-4 w-4" />
            {t(msg`Add ingredient`)}
          </Button>
        </div>

        <div class="space-y-2">
          <Label for="recipe-steps">{t(msg`Steps (one per line)`)}</Label>
          <textarea
            id="recipe-steps"
            class="flex min-h-32 w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
            value={steps()}
            onInput={(e) => setSteps(e.currentTarget.value)}
            data-testid="recipe-steps"
          />
        </div>

        {/* 🥗 Nutrición POR PORCIÓN (opcional): el planner suma por día
            escalando por las porciones planificadas de cada plato. */}
        <div class="space-y-2 rounded-xl border border-border p-4">
          <Label>{t(msg`Nutrition per serving (optional)`)}</Label>
          <div class="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div class="space-y-1">
              <Label for="recipe-calories" class="text-xs text-muted-foreground">
                {t(msg`Calories (kcal)`)}
              </Label>
              <TextField
                id="recipe-calories"
                type="number"
                min="0"
                inputmode="decimal"
                value={calories()}
                onInput={(e) => setCalories(e.currentTarget.value)}
                data-testid="recipe-calories"
              />
            </div>
            <div class="space-y-1">
              <Label for="recipe-protein" class="text-xs text-muted-foreground">
                {t(msg`Protein (g)`)}
              </Label>
              <TextField
                id="recipe-protein"
                type="number"
                min="0"
                inputmode="decimal"
                value={proteinG()}
                onInput={(e) => setProteinG(e.currentTarget.value)}
                data-testid="recipe-protein"
              />
            </div>
            <div class="space-y-1">
              <Label for="recipe-carbs" class="text-xs text-muted-foreground">
                {t(msg`Carbs (g)`)}
              </Label>
              <TextField
                id="recipe-carbs"
                type="number"
                min="0"
                inputmode="decimal"
                value={carbsG()}
                onInput={(e) => setCarbsG(e.currentTarget.value)}
                data-testid="recipe-carbs"
              />
            </div>
            <div class="space-y-1">
              <Label for="recipe-fat" class="text-xs text-muted-foreground">
                {t(msg`Fat (g)`)}
              </Label>
              <TextField
                id="recipe-fat"
                type="number"
                min="0"
                inputmode="decimal"
                value={fatG()}
                onInput={(e) => setFatG(e.currentTarget.value)}
                data-testid="recipe-fat"
              />
            </div>
          </div>
          <p class="text-xs text-muted-foreground">
            {t(msg`The weekly planner adds up each day's total from the servings you plan.`)}
          </p>
        </div>

        <div class="space-y-2">
          <Label for="recipe-notes">{t(msg`Notes`)}</Label>
          <textarea
            id="recipe-notes"
            class="flex min-h-16 w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
            value={notes()}
            onInput={(e) => setNotes(e.currentTarget.value)}
          />
        </div>

        <Show when={isEdit()}>
          <div class="space-y-2 rounded-xl border border-border p-4">
            <Label for="scale-preview">{t(msg`Scale to servings`)}</Label>
            <TextField
              id="scale-preview"
              type="number"
              min="1"
              class="w-32"
              placeholder={servings()}
              value={scalePreview()}
              onInput={(e) => setScalePreview(e.currentTarget.value)}
              data-testid="scale-preview-input"
            />
            <Show when={scaledIngredients()}>
              {(scaled) => (
                <ul
                  class="space-y-1 text-sm text-muted-foreground"
                  data-testid="scaled-ingredients"
                >
                  <For each={scaled()}>
                    {(item) => (
                      <li>
                        {item.quantity === null ? "" : `${item.quantity} ${item.unit} `}
                        {item.name}
                      </li>
                    )}
                  </For>
                </ul>
              )}
            </Show>
          </div>
        </Show>

        {/* Interconexión: dónde está planificada esta receta (sin callejón sin salida). */}
        <Show when={isEdit() && (plannedIn.data ?? []).length > 0}>
          <div class="space-y-2 rounded-xl border border-border p-4">
            <div class="flex items-center gap-2">
              <CalendarRange class="h-4 w-4 text-accent" />
              <Label>{t(msg`Planned in your week`)}</Label>
            </div>
            <ul class="flex flex-wrap gap-2" data-testid="recipe-planned-in">
              <For each={plannedIn.data ?? []}>
                {(entry) => (
                  <Link
                    to="/planner"
                    class="flex items-center gap-1 rounded-full bg-accent/10 px-3 py-1 text-xs font-medium text-accent transition-colors hover:bg-accent/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
                  >
                    <span class="capitalize">{formatPlanDate(entry.plan_date)}</span>
                    <span class="text-muted-foreground">·</span>
                    <span>{mealTypeLabel(entry.meal_type)}</span>
                  </Link>
                )}
              </For>
            </ul>
            <p class="text-xs text-muted-foreground">
              {t(msg`Tap an entry to open the weekly planner.`)}
            </p>
          </div>
        </Show>

        <ConfirmDialog
          open={confirmDelete()}
          onOpenChange={setConfirmDelete}
          title={t(msg`Move recipe to trash?`)}
          description={t(
            msg`It moves to the trash — restore it anytime from Profile → Recipe trash.`,
          )}
          confirmLabel={t(msg`Move to trash`)}
          variant="destructive"
          onConfirm={() => void handleDelete()}
        />

        <Show when={isEdit()}>
          <EntityHistoryDialog
            open={historyOpen()}
            onOpenChange={setHistoryOpen}
            entityType="recipe"
            entityId={recipeId()}
          />
          <RecipeVersionHistorySheet
            open={versionsOpen()}
            onOpenChange={setVersionsOpen}
            recipeId={recipeId}
            currentName={() => detail.data?.name ?? name()}
            currentIngredients={() => ingredients.data ?? []}
            currentSteps={() => parseStringArray(detail.data?.steps ?? null)}
            onRestore={(version) => void handleRestoreVersion(version)}
            restoring={restoringVersion}
          />
        </Show>
      </div>
    </Show>
  );
}
